import React from "react"
import { Trash2 } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

import type { DashboardCardType } from "../../../../types/dashboardLayout"
import { dashboardCardRegistry } from "../layout/dashboardCardRegistry"
import { preserveViewportScroll } from "./dashboardLayoutUi"
import { dashboardMutedGrayTextClassName } from "./dashboardChartCardShell"

export interface DashboardLayoutCardOptionsMenuProps {
  cardType: DashboardCardType
  replaceOptions: DashboardCardType[]
  canRemove: boolean
  onReplace: (nextCardType: DashboardCardType) => void
  onRemove: () => void
  children: React.ReactNode
}

function runAfterMenuClose(action: () => void): void {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }
  window.requestAnimationFrame(() => {
    preserveViewportScroll(action)
  })
}

export function DashboardLayoutCardOptionsMenu({
  cardType,
  replaceOptions,
  canRemove,
  onReplace,
  onRemove,
  children,
}: DashboardLayoutCardOptionsMenuProps): React.ReactElement {
  const currentTitle = dashboardCardRegistry[cardType].title
  const hasReplaceOptions = replaceOptions.length > 0

  return (
    <div data-dashboard-layout-card-options="" className="relative flex h-full min-h-0 w-full flex-1 flex-col">
      {children}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-dashboard-layout-card-trigger=""
            aria-label={`${currentTitle} options`}
            tabIndex={-1}
            className={cn(
              "absolute inset-0 z-10 cursor-pointer rounded-md border-2 border-transparent bg-transparent p-0",
              "hover:border-[color:var(--brand-tropical-surf)]/35 focus-visible:border-[color:var(--brand-tropical-surf)] focus-visible:outline-none"
            )}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="min-w-[14rem] font-[family-name:var(--font-primary)]"
          data-dashboard-layout-card-options-menu=""
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <DropdownMenuLabel className="font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]">
            {currentTitle}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {hasReplaceOptions ? (
            replaceOptions.map((optionCardType) => (
              <DropdownMenuItem
                key={optionCardType}
                className={cn("font-[family-name:var(--font-primary)] text-sm font-normal", dashboardMutedGrayTextClassName)}
                onSelect={() => runAfterMenuClose(() => onReplace(optionCardType))}
              >
                {dashboardCardRegistry[optionCardType].title}
              </DropdownMenuItem>
            ))
          ) : (
            <DropdownMenuItem disabled className={cn("font-[family-name:var(--font-primary)] text-sm font-normal", dashboardMutedGrayTextClassName)}>
              No other widgets fit here
            </DropdownMenuItem>
          )}
          {canRemove ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                className="font-[family-name:var(--font-primary)] text-sm font-normal"
                onSelect={() => runAfterMenuClose(onRemove)}
              >
                <Trash2 className="size-4" aria-hidden />
                Remove card
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
